import api from "./axios";
import {
  ApplyEstimatesResult,
  CapacityDailyResponse,
  CapacityDashboardResponse,
  CapacityOverrideViewModel,
  CapacityTimelineResponse,
  DayCapacityOverrideViewModel,
  EmployeeCapacityHistoryItem,
  EmployeeLoadItem,
  EstimateAllocationPreviewItem,
  HolidayViewModel,
  UnavailabilityViewModel,
  WeeklyAllocationViewModel,
} from "../interfaces/Capacity";

const BASE = "/Capacity";

const toIsoDate = (d?: Date | string | null) => {
  if (!d) return undefined;
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toISOString().slice(0, 10);
};

export async function getTimeline(
  start?: Date | string,
  weeks = 9
): Promise<CapacityTimelineResponse> {
  const { data } = await api.get(`${BASE}/timeline`, {
    params: { start: toIsoDate(start), weeks },
  });
  return data;
}

export async function getDashboard(
  week?: Date | string
): Promise<CapacityDashboardResponse> {
  const { data } = await api.get(`${BASE}/dashboard`, {
    params: { week: toIsoDate(week) },
  });
  return data;
}

export async function getDaily(
  start?: Date | string,
  days = 7
): Promise<CapacityDailyResponse> {
  const { data } = await api.get(`${BASE}/daily`, {
    params: { start: toIsoDate(start), days },
  });
  return data;
}

/** Tarefas (cliente/projeto + semana) que compõem o capacity de um colaborador. */
export async function getEmployeeLoad(
  employeeId: number,
  start?: Date | string,
  weeks = 9
): Promise<EmployeeLoadItem[]> {
  const { data } = await api.get(`${BASE}/employee-load`, {
    params: { employeeId, start: toIsoDate(start), weeks },
  });
  return Array.isArray(data) ? data : [];
}

// ── Allocations ──────────────────────────────────────────────────────────────

export async function getAllocationsByProject(
  projectId: number
): Promise<WeeklyAllocationViewModel[]> {
  const { data } = await api.get(`${BASE}/allocations/project/${projectId}`);
  return Array.isArray(data) ? data : [];
}

/** Alocações de todos os projetos (com TaskId). Sem intervalo, retorna todas. */
export async function getAllocations(
  start?: Date | string,
  weeks?: number
): Promise<WeeklyAllocationViewModel[]> {
  const { data } = await api.get(`${BASE}/allocations`, {
    params: { start: toIsoDate(start), weeks },
  });
  return Array.isArray(data) ? data : [];
}

export async function addAllocation(req: {
  employeeId: number;
  projectId: number;
  weekStart: string;
  allocatedHours: number;
  taskId?: number;
}): Promise<number> {
  const { data } = await api.post(`${BASE}/allocations`, req);
  return data?.id;
}

export async function updateAllocation(req: {
  id: number;
  allocatedHours: number;
}): Promise<void> {
  await api.put(`${BASE}/allocations`, req);
}

export async function deleteAllocation(id: number): Promise<void> {
  await api.delete(`${BASE}/allocations/${id}`);
}

// ── Capacity overrides ───────────────────────────────────────────────────────

export async function getOverrides(
  start?: Date | string,
  weeks = 9
): Promise<CapacityOverrideViewModel[]> {
  const { data } = await api.get(`${BASE}/overrides`, {
    params: { start: toIsoDate(start), weeks },
  });
  return Array.isArray(data) ? data : [];
}

export async function upsertOverride(req: {
  employeeId: number;
  weekStart: string;
  capacityHours: number;
  reason?: string;
}): Promise<number> {
  const { data } = await api.post(`${BASE}/overrides`, req);
  return data?.id;
}

export async function deleteOverride(id: number): Promise<void> {
  await api.delete(`${BASE}/overrides/${id}`);
}

// ── Unavailability ───────────────────────────────────────────────────────────

export async function getUnavailability(): Promise<UnavailabilityViewModel[]> {
  const { data } = await api.get(`${BASE}/unavailability`);
  return Array.isArray(data) ? data : [];
}

export async function addUnavailability(req: {
  employeeId: number;
  type: number;
  startDate: string;
  endDate: string;
  hoursImpacted?: number | null;
  description?: string;
}): Promise<number> {
  const { data } = await api.post(`${BASE}/unavailability`, req);
  return data?.id;
}

export async function deleteUnavailability(id: number): Promise<void> {
  await api.delete(`${BASE}/unavailability/${id}`);
}

// ── Ponte estimativa → alocação ──────────────────────────────────────────────

export async function getEstimatePreview(
  projectId: number,
  start?: Date | string,
  weeks = 9
): Promise<EstimateAllocationPreviewItem[]> {
  const { data } = await api.get(`${BASE}/allocations/estimate-preview/${projectId}`, {
    params: { start: toIsoDate(start), weeks },
  });
  return Array.isArray(data) ? data : [];
}

export async function applyEstimates(req: {
  projectId: number;
  start?: string;
  weeks?: number;
  overwrite?: boolean;
}): Promise<ApplyEstimatesResult> {
  const { data } = await api.post(`${BASE}/allocations/from-estimates`, req);
  return data;
}

// ── Feriados / dias sem expediente ────────────────────────────────────────────

export async function getHolidays(): Promise<HolidayViewModel[]> {
  const { data } = await api.get(`${BASE}/holidays`);
  return Array.isArray(data) ? data : [];
}

export async function addHoliday(req: { date: string; description?: string }): Promise<number> {
  const { data } = await api.post(`${BASE}/holidays`, req);
  return data?.id;
}

export async function deleteHoliday(id: number): Promise<void> {
  await api.delete(`${BASE}/holidays/${id}`);
}

// ── Capacity por dia (colaborador) ────────────────────────────────────────────

export async function getDayOverrides(
  start?: Date | string,
  days = 60
): Promise<DayCapacityOverrideViewModel[]> {
  const { data } = await api.get(`${BASE}/day-overrides`, {
    params: { start: toIsoDate(start), days },
  });
  return Array.isArray(data) ? data : [];
}

export async function upsertDayOverride(req: {
  employeeId: number;
  date: string;
  capacityHours: number;
  reason?: string;
}): Promise<number> {
  const { data } = await api.post(`${BASE}/day-overrides`, req);
  return data?.id;
}

export async function deleteDayOverride(id: number): Promise<void> {
  await api.delete(`${BASE}/day-overrides/${id}`);
}

// ── Employee baseline capacity ───────────────────────────────────────────────

export async function updateEmployeeCapacity(
  employeeId: number,
  weeklyCapacityHours: number
): Promise<void> {
  await api.put(`${BASE}/employees/${employeeId}/capacity`, { weeklyCapacityHours });
}

// ── Capacity padrão com vigência (não afeta o passado) ────────────────────────

export async function getEmployeeCapacityHistory(
  employeeId: number
): Promise<EmployeeCapacityHistoryItem[]> {
  const { data } = await api.get(`${BASE}/employees/${employeeId}/capacity-history`);
  return Array.isArray(data) ? data : [];
}

export async function setEmployeeCapacity(
  employeeId: number,
  weeklyCapacityHours: number,
  effectiveFrom?: string
): Promise<void> {
  await api.post(`${BASE}/employees/${employeeId}/capacity`, { weeklyCapacityHours, effectiveFrom });
}
